import { http } from "@/shared/lib/api/http"
import { getAuthContextState } from "@/features/auth/services/auth-context-store"
import {
  type BranchMemberPreview,
  type CreateBranchInput,
  type UpdateBranchInput,
  BranchStatus,
  BranchType,
} from "@/features/church/config/branches/types/branch.types"

type BranchPrincipalApiResponse = {
  id?: string | null
  memberId?: string | null
  name?: string | null
  fullName?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

export type BranchApiResponse = {
  id: string
  workspaceId?: string
  workspace_id?: string
  name: string
  code?: string | null
  branchType?: BranchType | null
  branch_type?: BranchType | null
  slug?: string | null
  status?: BranchStatus | string | null
  details?: string | null
  description?: string | null
  phoneNumber?: string | null
  phone_number?: string | null
  principalMemberId?: string | null
  principal_member_id?: string | null
  principalMember?: BranchPrincipalApiResponse | null
  principal_member?: BranchPrincipalApiResponse | null
  language?: string | null
  timezone?: string | null
  membersCount?: number | null
  teamCount?: number | null
  memberPreview?: BranchMemberPreview[] | null
  createdAt?: string
  created_at?: string
  updatedAt?: string
  updated_at?: string
}

type NormalizedBranchResponse = {
  id: string
  workspaceId?: string
  name: string
  code?: string
  slug?: string
  status: BranchStatus
  details?: string
  phoneNumber?: string
  branchType: BranchType
  principalMemberId?: string | null
  principalMember?: {
    id: string
    name: string
    email?: string | null
  } | null
  language?: string
  timezone?: string
  membersCount?: number
  teamCount?: number
  memberPreview?: BranchMemberPreview[]
  createdAt?: string
  updatedAt?: string
}

function normalizeString(value: unknown) {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeStatus(value: unknown) {
  return value === BranchStatus.INACTIVE ? BranchStatus.INACTIVE : BranchStatus.ACTIVE
}

function toNullableString(value: string) {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizePrincipalMember(input?: BranchPrincipalApiResponse | null) {
  if (!input) return null
  const id = normalizeString(input.id) ?? normalizeString(input.memberId)
  if (!id) return null
  const nameFromParts = normalizeString(
    [normalizeString(input.firstName), normalizeString(input.lastName)].filter(Boolean).join(" ")
  )
  const derivedName =
    normalizeString(input.name) ??
    normalizeString(input.fullName) ??
    nameFromParts ??
    id

  return {
    id,
    name: derivedName,
    email: input.email ?? null,
  }
}

function normalizeBranchResponse(input: BranchApiResponse): NormalizedBranchResponse {
  const principalMember = normalizePrincipalMember(input.principalMember ?? input.principal_member)
  const principalMemberId =
    normalizeString(input.principalMemberId) ??
    normalizeString(input.principal_member_id) ??
    principalMember?.id ??
    null

  return {
    id: input.id,
    workspaceId: normalizeString(input.workspaceId) ?? normalizeString(input.workspace_id),
    name: input.name,
    code: normalizeString(input.code),
    slug: normalizeString(input.slug),
    status: normalizeStatus(input.status),
    details: normalizeString(input.details) ?? normalizeString(input.description),
    phoneNumber: normalizeString(input.phoneNumber) ?? normalizeString(input.phone_number),
    branchType: input.branchType ?? input.branch_type ?? BranchType.Normal,
    principalMemberId,
    principalMember,
    language: normalizeString(input.language),
    timezone: normalizeString(input.timezone),
    membersCount: typeof input.membersCount === "number" ? input.membersCount : undefined,
    teamCount: typeof input.teamCount === "number" ? input.teamCount : undefined,
    memberPreview: Array.isArray(input.memberPreview) ? input.memberPreview : undefined,
    createdAt: input.createdAt ?? input.created_at,
    updatedAt: input.updatedAt ?? input.updated_at,
  }
}

export async function listBranchesService(
  input: { workspaceId?: string; status?: BranchStatus; includeSystem?: boolean } = {}
) {
  const workspaceId = input.workspaceId ?? getAuthContextState().activeWorkspaceId ?? undefined
  // The branches list backs a global store (branch selectors, finance scope), so
  // it must hold the full set rather than one server page. Request the backend
  // max so the previous default-20 cap no longer hides branches; the config
  // table then client-paginates over the complete list.
  const params: Record<string, string> = { limit: "100" }
  if (workspaceId) {
    params.workspaceId = workspaceId
  }
  if (input.status) {
    params.status = input.status
  }
  if (input.includeSystem) {
    params.includeSystem = "true"
  }

  const response = await http.get<BranchApiResponse[] | { items?: BranchApiResponse[] }>("/branches", {
    params,
  })

  const payload = response.data
  const items = Array.isArray(payload) ? payload : Array.isArray(payload.items) ? payload.items : []
  return items.map(normalizeBranchResponse)
}

export async function createBranchService(input: CreateBranchInput) {
  const workspaceId = getAuthContextState().activeWorkspaceId
  if (!workspaceId) {
    throw new Error("errors.noWorkspace")
  }

  const payload: Record<string, unknown> = {
    workspaceId: workspaceId,
    name: input.name.trim(),
  }
  if (input.code !== undefined) {
    payload.code = input.code.trim()
  }

  if (input.status) {
    payload.status = input.status
  }
  if (input.details !== undefined) {
    payload.details = toNullableString(input.details)
  }
  if (input.phoneNumber !== undefined) {
    payload.phoneNumber = toNullableString(input.phoneNumber)
  }
  if (input.principalMemberId !== undefined) {
    payload.principalMemberId = input.principalMemberId?.trim() ? input.principalMemberId.trim() : null
  }
  if (input.branchType !== undefined) {
    payload.branchType = input.branchType
  }
  if (input.language !== undefined) {
    payload.language = input.language
  }
  if (input.timezone !== undefined) {
    payload.timezone = input.timezone
  }
  if (input.customFieldValues !== undefined) {
    payload.customFieldValues = input.customFieldValues
  }
  if (input.grantCreatorAccess !== undefined) {
    payload.grantCreatorAccess = input.grantCreatorAccess
  }

  const response = await http.post<BranchApiResponse>("/branches", {
    ...payload,
  })
  return normalizeBranchResponse(response.data)
}

export async function updateBranchService(id: string, input: UpdateBranchInput) {
  const payload: Record<string, unknown> = {}

  if (input.name !== undefined) {
    payload.name = input.name.trim()
  }
  if (input.details !== undefined) {
    payload.details = toNullableString(input.details)
  }
  if (input.phoneNumber !== undefined) {
    payload.phoneNumber = toNullableString(input.phoneNumber)
  }
  if (input.principalMemberId !== undefined) {
    payload.principalMemberId = input.principalMemberId?.trim() ? input.principalMemberId.trim() : null
  }
  if (input.status !== undefined) {
    payload.status = input.status
  }
  if (input.branchType !== undefined) {
    payload.branchType = input.branchType
  }
  if (input.language !== undefined) {
    payload.language = input.language
  }
  if (input.timezone !== undefined) {
    payload.timezone = input.timezone
  }
  if (input.customFieldValues !== undefined) {
    payload.customFieldValues = input.customFieldValues
  }

  const response = await http.patch<BranchApiResponse>(`/branches/${id}`, payload)
  return normalizeBranchResponse(response.data)
}

export async function setBranchStatusService(id: string, status: BranchStatus) {
  const response = await http.patch<BranchApiResponse>(`/branches/${id}`, {
    status,
  })
  return normalizeBranchResponse(response.data)
}
